/* Course home — artboard 2a, "region bands".

   Every lesson in the course is visible at once. The active unit inverts so the
   student's current position is findable without reading.
*/

import s from './CourseHome.module.css';
import { Kicker, Meta, Button, Meter } from '../design/primitives.jsx';
import { kindLabel, statusLabel, statusLabelNoProgress } from '../design/tokens.js';
import { unitName, totalWeeks as raw_weeks, totalModules } from '../content/course.js';
import { useProgress } from '../lib/progress.jsx';
import { useTrack, TRACKS } from '../lib/track.jsx';
import { termState, shortDate } from '../lib/term.js';
import { TERM } from '../config.js';

const cx = (...v) => v.filter(Boolean).join(' ');

/* The node's shape says what kind of lesson it is; its fill is reserved for
   completion, so every node is hollow until progress tracking lands. Two axes,
   two different visual properties — which is what the earlier version got
   wrong by spending fill and shape on the same question. */
const NODE = {
  trial: <path d="M7 2.1 11.6 10.3 2.4 10.3Z" strokeLinejoin="round" />,
  check: <rect x="2.7" y="2.7" width="8.6" height="8.6" rx="1.3" />,
  assignment: <path d="M7.00 1.80 8.44 5.42 12.33 5.67 9.33 8.16 10.29 11.93 7.00 9.85 3.71 11.93 4.67 8.16 1.67 5.67 5.56 5.42Z" strokeLinejoin="round" />,
};
const circle = <circle cx="7" cy="7" r="4.4" />;

/* One lesson, one row.

   This replaced a card in a wrapping grid. A week is a sequence, and a grid of
   equal tiles says the opposite — seven things, in an order the eye has to
   rebuild left-to-right then top-to-bottom. A single column with a spine down
   it states the order once, gives the titles room to be sentences instead of
   three wrapped lines, and lets the checkpoint close the week visibly. */
function LessonRow({ lesson, onOpen }) {
  const { status: st, kind } = lesson;
  const { enabled: progressOn } = useProgress();
  const label = (progressOn ? statusLabel : statusLabelNoProgress)[st] ?? '';
  const locked = st === 'locked';
  /* `next` means "the one after the one you are on", which needs a you-are-on
     to be true. With progress off it is permanently lesson two, so it carries
     no emphasis — see the commit that removed its gold fill. */
  const mark = progressOn || st !== 'next' ? st : 'open';
  /* Three tiers, not four kinds: intake, practice, and the one that counts.
     `video` is intake, so it takes no marking beyond its label. */
  const tier = kind === 'check' ? s.isCheck : kind === 'trial' ? s.isTrial : kind === 'assignment' ? s.isAssignment : null;
  return (
    <button
      type="button"
      disabled={locked}
      onClick={() => onOpen?.(lesson)}
      className={cx(s.row, tier, locked && s.rowLocked,
                    mark === 'active' && s.rowActive,
                    mark === 'next' && s.rowNext,
                    st === 'done' && s.rowDone)}
      aria-label={`${lesson.title} — ${kindLabel[kind].toLowerCase()}, ${(label || 'available').toLowerCase()}`}>
      <span className={s.node} aria-hidden="true">
        <svg className={s.nodeMark} viewBox="0 0 14 14" width="14" height="14" focusable="false">
          {NODE[kind] ?? circle}
        </svg>
      </span>
      <span className={s.rowTitle}>{lesson.title}</span>
      <span className={cx(s.rowStatus, st === 'done' && s.done, locked && s.lockedText)}>{label}</span>
      <span className={s.rowKind}>{kindLabel[kind]}</span>
    </button>
  );
}

const UnitBand = ({ unit, onOpen }) => (
  <section className={cx(s.band, s[unit.status])} aria-labelledby={`unit-${unit.id}`}>
    <div>
      <div className={s.bandNum}>{unit.num}</div>
      <h2 id={`unit-${unit.id}`} className={s.bandName}>{unitName(unit)}</h2>
      {unit.meta && <div className={s.bandMeta}>{unit.meta}</div>}
      <p className={s.bandBlurb}>{unit.blurb}</p>
      <div className={s.bandCount}>{unit.lessons.length} lessons</div>
    </div>
    <div className={s.track}>
      {unit.lessons.map(l => <LessonRow key={l.id} lesson={l} onOpen={onOpen} />)}
    </div>
  </section>
);

/* Where the cohort is in the quarter. The one place the site acknowledges that
   a term exists — everything else is the same whenever you read it.

   The denominator is the course's real week count, not the published one: the
   cohort is in week 2 of 11 whether or not week 2 is on the site yet. Passing
   `course.units.length` here rendered "Week 1/1" in October. */
function TermPanel({ totalWeeks }) {
  const t = termState(TERM, totalWeeks);
  if (t.phase === 'before') {
    return (
      <div className={s.progress}>
        <div className={s.progressLabel}>TERM BEGINS</div>
        <div className={s.progressCount}>{shortDate(t.start)}</div>
        <div className={s.progressLeft}>
          {t.daysUntil === 1 ? 'tomorrow' : `in ${t.daysUntil} days`}
        </div>
      </div>
    );
  }
  if (t.phase === 'after') {
    return (
      <div className={s.progress}>
        <div className={s.progressLabel}>TERM ENDED</div>
        <div className={s.progressCount}>{shortDate(t.end)}</div>
        <div className={s.progressLeft}>the material stays up</div>
      </div>
    );
  }
  return (
    <div className={s.progress}>
      <div className={s.progressLabel}>THIS TERM</div>
      <div className={s.progressCount}>Week {t.week}<span>/{t.totalWeeks}</span></div>
      <div className={s.progressLeft}>
        {t.daysLeft === 0 ? 'last day' : `${t.daysLeft} day${t.daysLeft === 1 ? '' : 's'} left`}
      </div>
    </div>
  );
}

export default function CourseHome({ course, stats, onOpen, onOpenSyllabus }) {
  /* Count teaching modules only: Orientation (05-module rule 15a) and the final
     checkpoint are units, not modules. */
  const modUnits = course.units.filter(u => u.id !== 'start-here' && u.id !== 'final').length;
  const { enabled: progressOn } = useProgress();
  const [track] = useTrack();
  const trackLabel = TRACKS.find(t => t.id === track)?.label;
  return (
    <div className={s.home}>
      <header className={s.header}>
        <div className={s.headerTop}>
          <div>
            <Kicker spacing="wider">
              {course.code} · {modUnits} {modUnits === 1 ? 'MODULE' : 'MODULES'} · {stats.total} LESSONS
            </Kicker>
            <h1 className={s.title}>{course.title}</h1>
            <p className={s.blurb}>{course.blurb}</p>
          </div>
          {/* A meter that always reads 0 and a "remaining" that never falls are
              worse than no meter. With progress off this slot carries the one
              thing that is true for everyone — where the cohort is in the
              quarter — rather than restating the course's size, which the
              kicker above already gives. */}
          {progressOn ? (
            <div className={s.progress}>
              <div className={s.progressLabel}>PROGRESS</div>
              <div className={s.progressCount}>{stats.cleared}<span>/{stats.total}</span></div>
              <Meter pct={stats.pct} className={s.progressMeter} />
            </div>
          ) : (
            <TermPanel totalWeeks={raw_weeks} />
          )}
        </div>
        {/* No "Start" / "Resume" button: with progress off it always named the
            first lesson, and a button that never changes reads as broken once
            a student is weeks in. The track is shown, not chosen — a picker
            comes when a second track ships (config.LANGUAGES). */}
        <div className={s.actions}>
          <Button variant="secondary" onClick={onOpenSyllabus}>Full syllabus</Button>
          {trackLabel && <span className={s.trackTag} aria-label={`Language: ${trackLabel}`}>{trackLabel}</span>}
        </div>
      </header>

      {course.units.map(u => <UnitBand key={u.id} unit={u} onOpen={onOpen} />)}

      <footer className={s.footer}>
        {/* The footnote promises checkpoints. While the course is published a
            few modules at a time, promising one the reader cannot reach is
            worse than saying nothing. */}
        <span>{modUnits === totalModules ? course.footnote : `${modUnits} of ${totalModules} modules published — more each week.`}</span>
        <Meta>last updated {course.updated}</Meta>
      </footer>
    </div>
  );
}
